import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Gere l'ajout au panier de la fiche SofaGuard quand le bundle
 * (sg-bundle-selector, voir sg-bundle-selector.js) est sur un palier 2/3/4
 * housses. Copie independante adaptee de cs-bundle-add-to-cart.js
 * (CleanyStep, jamais touchee ici) - meme mecanisme exact (voir ce fichier
 * pour le detail des 2 bugs deja trouves/corriges : stopImmediatePropagation
 * insuffisant seul contre le gestionnaire natif, corrige en repassant le
 * bouton en type="button" des que le palier >= 2 ; paiement express masque
 * pareil des que le palier >= 2, pour la meme raison), seules les
 * proprietes de ligne panier changent de libelle ("Housse N", pas
 * "Tapis N" - SofaGuard est une housse de canape).
 */
function init() {
  const bundle = document.querySelector('sg-bundle-selector');
  if (!bundle) return;

  const section = bundle.closest('.shopify-section');
  const form = section?.querySelector('form[data-type="add-to-cart-form"]');
  if (!(form instanceof HTMLFormElement)) return;

  const submitButton = form.querySelector('button[type="submit"]');
  if (!(submitButton instanceof HTMLButtonElement)) return;

  /** @returns {Element[]} */
  function expressCheckoutElements() {
    return Array.from(
      section.querySelectorAll('shopify-accelerated-checkout, [id^="accelerated-checkout" i]')
    );
  }

  function syncButtonMode() {
    const { tier } = bundle.getSelection();
    submitButton.type = tier === 1 ? 'submit' : 'button';
    expressCheckoutElements().forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      if (tier === 1) el.style.removeProperty('display');
      else el.style.setProperty('display', 'none', 'important');
    });
  }

  bundle.addEventListener('change', syncButtonMode);
  syncButtonMode();

  submitButton.addEventListener('click', (event) => {
    if (submitButton.type !== 'button') return; // palier 1 : formulaire natif, rien a faire ici
    event.preventDefault();
    handleBundleAddToCart(bundle, form, submitButton);
  });
}

/**
 * @param {HTMLElement & {getSelection: () => {tier: 1|2|3|4, size: string, colors: string[]}, tierData: Record<string, Record<string, {variantId: string, price: number, priceFormatted: string}>>}} bundle
 * @param {HTMLFormElement} form
 * @param {HTMLButtonElement} submitButton
 */
function handleBundleAddToCart(bundle, form, submitButton) {
  const selection = bundle.getSelection();
  const tierEntry = bundle.tierData?.[String(selection.tier)]?.[selection.size];
  if (!tierEntry) {
    console.error('[sg-bundle-add-to-cart] Aucune variante trouvee pour', selection);
    return;
  }

  const quantityInput = form.querySelector('input[name="quantity"]');
  const quantity = quantityInput instanceof HTMLInputElement ? Number(quantityInput.value) || 1 : 1;

  /** @type {Record<string, string>} */
  const properties = {};
  selection.colors.forEach((color, index) => {
    properties[`Protection ${index + 1}`] = color;
  });

  submitButton.disabled = true;

  const addPromise = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: tierEntry.variantId.toString(), quantity }],
      promise: addPromise.promise,
    })
  );

  fetch(
    Theme.routes.cart_add_url,
    fetchConfig('json', {
      body: JSON.stringify({
        items: [{ id: tierEntry.variantId, quantity, properties }],
        sections: cartSectionIds().join(','),
      }),
    })
  )
    .then((response) => response.json())
    .then((response) => {
      if (response.status) {
        addPromise.reject(new Error(response.message || 'Add to cart failed'));
        form.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Impossible d’ajouter ce bundle au panier.',
            code: 'INVALID',
          })
        );
        return;
      }
      resolveWithFullCart(addPromise, response);
    })
    .catch((error) => {
      console.error('[sg-bundle-add-to-cart]', error);
      addPromise.reject(error);
      form.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Erreur reseau lors de l’ajout au panier',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    })
    .finally(() => {
      submitButton.disabled = false;
    });
}

/** @returns {string[]} */
function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
    .filter(/** @returns {id is string} */ (id) => Boolean(id));
}

/**
 * @param {ReturnType<typeof CartLinesUpdateEvent.createPromise>} promiseHandle
 * @param {{sections?: unknown, items?: unknown}} addResponse
 */
async function resolveWithFullCart(promiseHandle, addResponse) {
  let cart = null;
  try {
    const fullCart = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => response.json());
    cart = CartLinesUpdateEvent.createCartFromAjaxResponse(fullCart);
  } catch (error) {
    console.warn('[sg-bundle-add-to-cart] full cart fetch/conversion failed, continuing anyway', error);
  }

  promiseHandle.resolve({
    cart,
    detail: {
      sections: addResponse.sections,
      items: addResponse.items,
      source: 'sg-bundle-add-to-cart',
      didError: false,
    },
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
